import React, { useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PanelRegister from 'v3/pages/PowerBi/PanelRegister';
import { UserContext } from 'contexts/UserContext';
import { LoadingPage } from 'v3/pages/LoadingPage';
import { UpdatePasswordPage } from 'v3/pages/UpdatePassword';
import { mustChangePassword } from 'v3/utils/password';
import { FlowChatHost } from 'v3/integrations/flowChat/FlowChatHost';
import { toScreenRoutes } from 'v3/integrations/flowChat/screenHints';
import { isAuthenticated } from './services/auth';
import routes from './config/MenuNavConstants';
import ForgotPassword from './pages/forgotPassword';
import Login from './pages/login/Login';
import ApplicationLayoutV3 from './v3/layouts/Application';
import PrivateRouteV3 from './v3/routes/PrivateRoute';
import DevComponents from './v3/pages/dev/index';
import AppRedirectPage from './v3/pages/AppRedirectPage';
import PublicSingleChecklist from 'v3/pages/Checklist/CheckListSingle/ActiveLinkList/PublicResponse';
import RouteElement from './router/RouteElement';
import PublicTrainingChecklistResponsePage from 'v3/pages/Checklist/TrainingChecklist/PublicResponse ';

const PrivateRoute = ({ title, component: Component, path }) => {
  const dispatch = useDispatch();
  const {
    isUserActive,
    isLoading,
    shouldUserChangePassword,
    lastPasswordUpdate,
    isUserBlocked,
    isCooperMail,
  } = useContext(UserContext);

  const authenticated = isAuthenticated();
  const shouldRedirectToChangePasswordModal = mustChangePassword({
    isCooperMail,
    isUserActive,
    isUserBlocked,
    authenticated,
    shouldUserChangePassword,
    lastPasswordUpdate,
  });

  if (isLoading) {
    return <LoadingPage isLoading={isLoading} />;
  }

  if (shouldRedirectToChangePasswordModal) {
    return <UpdatePasswordPage />;
  }

  if (isUserActive && authenticated && !isUserBlocked) {
    dispatch({ type: 'CURRENT_PATH', path });

    return (
      <ApplicationLayoutV3 title={title} isOld>
        <div className="w-100 pt-3">
          <RouteElement component={Component} path={path} />
        </div>
      </ApplicationLayoutV3>
    );
  }

  localStorage.removeItem('token');
  localStorage.removeItem('usuario');
  localStorage.removeItem('updatePushUserId');
  localStorage.removeItem('permissions');
  localStorage.removeItem('bot');
  localStorage.removeItem('issue_permission');

  return <Navigate to="/login" replace />;
};

const allRoutes = routes
  .filter((route) => route.children)
  .reduce(
    (acc, route) => {
      route.children.forEach((children) => acc.push(children));
      return acc;
    },
    [...routes],
  )
  .filter((route) => route.path);

const flowChatScreens = toScreenRoutes(routes);

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<RouteElement component={Login} path="/login" />} />
        <Route
          path="/forgot-password/:token"
          element={<RouteElement component={ForgotPassword} path="/forgot-password/:token" />}
        />
        <Route
          path="/checklist/public/:token"
          element={
            <RouteElement component={PublicSingleChecklist} path="/checklist/public/:token" />
          }
        />
        <Route
          path="/treinamento/public/:token"
          element={
            <RouteElement
              component={PublicTrainingChecklistResponsePage}
              path="/treinamento/public/:token"
            />
          }
        />

        <Route
          path="/panel-register-edit"
          element={<RouteElement component={PanelRegister} path="/panel-register-edit" />}
        />
        <Route
          path="/load-offer/:loadCode"
          element={<RouteElement component={AppRedirectPage} path="/load-offer/:loadCode" />}
        />
        <Route
          path="/aggregation-invite/:aggregationCode"
          element={
            <RouteElement component={AppRedirectPage} path="/aggregation-invite/:aggregationCode" />
          }
        />

        {import.meta.env.VITE_STAGE === 'dev' && (
          <Route
            path="/dev/components"
            element={<PrivateRouteV3 component={DevComponents} path="/dev/components" />}
          />
        )}

        {allRoutes.map((route, index) => {
          if (route.new) {
            return (
              <Route
                key={route.path}
                path={route.path}
                element={<PrivateRouteV3 component={route.component} path={route.path} />}
              />
            );
          }

          return (
            <Route
              key={String(index)}
              path={route.path}
              element={
                <PrivateRoute path={route.path} component={route.component} title={route.title} />
              }
            />
          );
        })}
      </Routes>
      <FlowChatHost routes={flowChatScreens} />
    </BrowserRouter>
  );
}

export default AppRoutes;
